'use client';

import {
  resolveContactSavePlan,
  type ContactSavePlan,
} from '@up2stay/up2promo-commons';
import { ArrowLeftRight, Contact, Mail } from 'lucide-react';
import { useEffect, useState, type MouseEvent, type Ref } from 'react';

import { cn } from '../../utils/cn';
import type { ProfileTokenPalette } from '../profile-accent-color';
import type { ProfileThemeMode } from '../profile-accent-theme';
import type {
  PublicProfileButtonShape,
  PublicProfileLayout,
} from '../public-profile-layout';
import {
  resolveSaveContactBlockedCopy,
  SaveContactInAppGuide,
  type SaveContactBlockedCopy,
} from '../save-contact-in-app-guide';

type ProfileCtaButtonsProps = {
  layout: PublicProfileLayout;
  buttonShape?: PublicProfileButtonShape | null;
  showExchange?: boolean;
  exchangeLabel: string;
  onExchange?: () => void;
  exchangeRef?: Ref<HTMLButtonElement>;
  saveContactUrl?: string | null;
  saveContactLabel: string;
  saveContactBlocked?: SaveContactBlockedCopy;
  accentColor?: string | null;
  theme?: ProfileThemeMode | null;
  palette?: ProfileTokenPalette;
  /** When true, CTAs sit in the header row (no top margin). */
  embedded?: boolean;
  className?: string;
};

function radiusFor(
  layout: PublicProfileLayout,
  buttonShape?: PublicProfileButtonShape | null,
): string {
  if (layout === 'centered' || buttonShape === 'pill') return 'rounded-full';
  if (buttonShape === 'square') return 'rounded-md';
  if (layout === 'classic') return 'rounded-[13px]';
  return 'rounded-[15px] @min-[880px]/profile:rounded-[13px]';
}

function canShareVcf(vcf: string): File | null {
  if (typeof navigator.share !== 'function') return null;
  if (typeof navigator.canShare !== 'function') return null;
  const file = new File([vcf], 'contact.vcf', { type: 'text/vcard' });
  try {
    return navigator.canShare({ files: [file] }) ? file : null;
  } catch {
    return null;
  }
}

export function ProfileCtaButtons({
  layout,
  buttonShape,
  showExchange,
  exchangeLabel,
  onExchange,
  exchangeRef,
  saveContactUrl,
  saveContactLabel,
  saveContactBlocked,
  accentColor,
  theme,
  palette = 'standard',
  embedded = false,
  className,
}: ProfileCtaButtonsProps) {
  const [vcfText, setVcfText] = useState<string | null>(null);
  const [guideOpen, setGuideOpen] = useState(false);
  const [guidePlan, setGuidePlan] = useState<Extract<
    ContactSavePlan,
    { kind: 'in-app' }
  > | null>(null);

  useEffect(() => {
    if (!saveContactUrl) return;
    if (resolveContactSavePlan(navigator.userAgent).kind !== 'in-app') return;

    const controller = new AbortController();
    void fetch(saveContactUrl, {
      signal: controller.signal,
      credentials: 'omit',
    })
      .then((response) => (response.ok ? response.text() : null))
      .then((text) => {
        if (text?.includes('BEGIN:VCARD')) setVcfText(text);
      })
      .catch(() => {
        // In-app browsers can block the cross-origin fetch. The guide still
        // explains how to open the profile in the system browser.
      });

    return () => controller.abort();
  }, [saveContactUrl]);

  if (!showExchange && !saveContactUrl) return null;

  const openGuide = (plan: Extract<ContactSavePlan, { kind: 'in-app' }>) => {
    setGuidePlan(plan);
    setGuideOpen(true);
  };

  const onSaveContactClick = (event: MouseEvent<HTMLAnchorElement>) => {
    const plan = resolveContactSavePlan(navigator.userAgent);
    if (plan.kind === 'browser') return;

    event.preventDefault();
    const file = vcfText ? canShareVcf(vcfText) : null;
    if (!file) {
      openGuide(plan);
      return;
    }

    void navigator
      .share({ files: [file], title: saveContactLabel })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === 'AbortError') {
          return;
        }
        openGuide(plan);
      });
  };

  const radius = radiusFor(layout, buttonShape);
  const primaryClass = cn(
    'inline-flex min-h-11 w-full cursor-pointer items-center justify-center gap-[9px] border-none px-[22px] py-[15px] text-[14px] font-semibold text-[var(--accent-text)] shadow-[0_10px_26px_var(--accent-16)]',
    'bg-[image:var(--accent-grad)] transition-[transform,box-shadow] duration-150',
    'hover:bg-[image:var(--accent-grad-hover)] active:scale-[0.99] active:bg-[image:var(--accent-grad-hover)]',
    layout === 'cards' && 'text-[14.5px] @min-[880px]/profile:text-[14px]',
    radius,
  );
  const secondaryClass = cn(
    'inline-flex min-h-11 cursor-pointer items-center justify-center gap-[9px] border border-[var(--border)] bg-[var(--surface2)] px-[22px] py-3.5 text-[14px] font-semibold text-[var(--text)]',
    'transition-[background-color,border-color,transform] duration-150',
    'hover:border-[color-mix(in_oklab,var(--accent)_28%,var(--border))] hover:bg-[var(--surface)] active:scale-[0.99]',
    radius,
  );

  const guide = saveContactUrl ? (
    <SaveContactInAppGuide
      open={guideOpen}
      onOpenChange={setGuideOpen}
      copy={saveContactBlocked ?? resolveSaveContactBlockedCopy()}
      appLabel={guidePlan?.appLabel ?? null}
      platform={guidePlan?.platform ?? 'other'}
      pageUrl={
        guideOpen && typeof window !== 'undefined' ? window.location.href : ''
      }
      accentColor={accentColor}
      theme={theme}
      palette={palette}
    />
  ) : null;

  if (layout === 'classic') {
    // Widths follow the 880px header row via CSS (not a JS embed flip).
    // Margin is 0 when embedded in the header flex (gap handles rhythm).
    return (
      <>
        <div className={cn('mt-0 flex gap-2.5', className)}>
          {showExchange ? (
            <button
              ref={exchangeRef}
              type="button"
              onClick={onExchange}
              className={cn(
                primaryClass,
                'flex-1 shadow-[0_8px_20px_var(--accent-16)] @min-[880px]/profile:w-auto @min-[880px]/profile:flex-none',
              )}
            >
              <ArrowLeftRight
                className="h-4 w-4"
                style={{ stroke: 'var(--accent-text)' }}
                strokeWidth={1.9}
              />
              {exchangeLabel}
            </button>
          ) : null}
          {saveContactUrl ? (
            <a
              href={saveContactUrl}
              onClick={onSaveContactClick}
              className={cn(
                secondaryClass,
                'w-[54px] shrink-0 border-[1.5px] bg-transparent px-0 @min-[880px]/profile:w-auto @min-[880px]/profile:px-[22px]',
                !showExchange && 'flex-1 @min-[880px]/profile:flex-none',
              )}
              aria-label={saveContactLabel}
            >
              <Contact
                className="h-[18px] w-[18px] @min-[880px]/profile:h-[17px] @min-[880px]/profile:w-[17px]"
                style={{ stroke: 'var(--text)' }}
                strokeWidth={1.8}
              />
              {/* As the only action it fills the row, so show its label on
                  mobile too; next to „Kontakt austauschen“ it stays an icon
                  (DEV-539). */}
              {!showExchange ? <span>{saveContactLabel}</span> : null}
            </a>
          ) : null}
        </div>
        {guide}
      </>
    );
  }

  if (layout === 'centered') {
    return (
      <>
        <div
          className={cn(
            'flex w-full flex-col gap-2.5',
            embedded ? 'mt-0' : 'mt-6',
            className,
          )}
        >
          {showExchange ? (
            <button
              ref={exchangeRef}
              type="button"
              onClick={onExchange}
              className={primaryClass}
            >
              <ArrowLeftRight
                className="h-[17px] w-[17px]"
                style={{ stroke: 'var(--accent-text)' }}
                strokeWidth={1.9}
              />
              {exchangeLabel}
            </button>
          ) : null}
          {saveContactUrl ? (
            <a
              href={saveContactUrl}
              onClick={onSaveContactClick}
              className={secondaryClass}
            >
              <Contact
                className="h-[17px] w-[17px]"
                style={{ stroke: 'var(--text)' }}
                strokeWidth={1.7}
              />
              {saveContactLabel}
            </a>
          ) : null}
        </div>
        {guide}
      </>
    );
  }

  // cards — stacked on mobile, row on desktop (inside header)
  return (
    <>
      <div
        className={cn(
          'flex w-full flex-col gap-2.5 @min-[880px]/profile:w-auto @min-[880px]/profile:flex-row @min-[880px]/profile:gap-3',
          embedded ? 'mt-5' : 'mt-4 @min-[880px]/profile:mt-5',
          className,
        )}
      >
        {showExchange ? (
          <button
            ref={exchangeRef}
            type="button"
            onClick={onExchange}
            className={cn(primaryClass, '@min-[880px]/profile:w-auto')}
          >
            <Mail
              className="h-[17px] w-[17px]"
              style={{ stroke: 'var(--accent-text)' }}
              strokeWidth={1.9}
            />
            {exchangeLabel}
          </button>
        ) : null}
        {saveContactUrl ? (
          <a
            href={saveContactUrl}
            onClick={onSaveContactClick}
            className={cn(secondaryClass, 'w-full @min-[880px]/profile:w-auto')}
          >
            <Contact
              className="h-[17px] w-[17px]"
              style={{ stroke: 'var(--text)' }}
              strokeWidth={1.7}
            />
            {saveContactLabel}
          </a>
        ) : null}
      </div>
      {guide}
    </>
  );
}
